import { LaudoStep, LaudoWizardData, IdentificacaoData, ChecklistData, VistoriaData, AvaliacaoData, AmostrasData, SignatureData, TransmissaoData, WizardMode, WizardReviewContext } from '../../types/laudo-wizard.types';
import { InspectionRequest } from '@/services/types/inspection.types';

import { ChecklistStep } from './steps/ChecklistStep';
import { IdentificacaoStep } from './steps/IdentificacaoStep';
import { VistoriaStep } from './steps/VistoriaStep';
import { AvaliacaoStep } from './steps/AvaliacaoStep';
import { AmostrasStep } from './steps/AmostrasStep';
import { SignatureStep } from './steps/SignatureStep';
import { TransmissaoStep } from './steps/TransmissaoStep';

interface UseStepRendererProps {
  currentStep: LaudoStep;
  laudoData: LaudoWizardData;
  inspection: InspectionRequest;
  signatureDataLoaded: boolean;
  hasCompletedVideoCall: boolean;
  goToNext: () => void;
  updateChecklist: (data: Partial<ChecklistData>) => void;
  updateIdentificacao: (data: Partial<IdentificacaoData>) => void;
  updateVistoria: (data: Partial<VistoriaData>) => void;
  updateAvaliacao: (data: Partial<AvaliacaoData>) => void;
  updateAmostras: (data: Partial<AmostrasData>) => void;
  updateSignature: (data: Partial<SignatureData>) => void;
  updateTransmissao: (data: Partial<TransmissaoData>) => void;
  mode?: WizardMode;
  reviewContext?: WizardReviewContext;
  /** Modo apenas checklist - para inspetor preencher checklist e fechar (sem gerar laudo) */
  checklistOnlyMode?: boolean;
  /** Callback chamado quando o checklist é completado no modo checklist-only */
  onChecklistCompleted?: () => void;
  /** Callback para pular cálculos (enviar laudo sem amostras) */
  onSkipCalculations?: () => void;
}

export function useStepRenderer({
  currentStep,
  laudoData,
  inspection,
  signatureDataLoaded,
  hasCompletedVideoCall,
  goToNext,
  updateChecklist,
  updateIdentificacao,
  updateVistoria,
  updateAvaliacao,
  updateAmostras,
  updateSignature,
  updateTransmissao,
  mode = 'create',
  reviewContext,
  checklistOnlyMode = false,
  onChecklistCompleted,
  onSkipCalculations,
}: UseStepRendererProps) {
  const renderStep = () => {
    // No modo calculations, todos os steps são editáveis
    // (banco/prestadora pode corrigir informações do laudo além dos cálculos)
    const effectiveMode = (_step: LaudoStep): WizardMode => {
      return mode;
    };


    const avaliacaoStepElement = (
      <div style={{ display: currentStep === LaudoStep.AVALIACAO ? 'block' : 'none' }}>
        <AvaliacaoStep
          data={laudoData.avaliacao}
          onUpdate={updateAvaliacao}
          inspection={inspection}
          mode={effectiveMode(LaudoStep.AVALIACAO)}
        />
      </div>
    );

    const renderActiveStep = () => {
      switch (currentStep) {
        case LaudoStep.CHECKLIST:
          if (!hasCompletedVideoCall && !checklistOnlyMode) {
            return null;
          }
          // No modo checklist-only, usar onChecklistCompleted (que fecha o wizard)
          // Caso contrário, usar goToNext (que avança para o próximo step)
          const checklistOnNext = checklistOnlyMode && onChecklistCompleted
            ? onChecklistCompleted
            : goToNext;
          return (
            <ChecklistStep
              data={laudoData.checklist}
              inspection={inspection}
              onUpdate={updateChecklist}
              onNext={checklistOnNext}
              correctionMode={checklistOnlyMode}
            />
          );

        case LaudoStep.IDENTIFICACAO:
          return (
            <IdentificacaoStep
              data={laudoData.identificacao}
              onUpdate={updateIdentificacao}
              inspection={inspection}
              mode={effectiveMode(LaudoStep.IDENTIFICACAO)}
            />
          );

        case LaudoStep.VISTORIA:
          return (
            <VistoriaStep
              data={laudoData.vistoria}
              onUpdate={updateVistoria}
              inspection={inspection}
              identificacaoData={laudoData.identificacao}
              mode={effectiveMode(LaudoStep.VISTORIA)}
            />
          );

        case LaudoStep.AVALIACAO:
          return null;

        case LaudoStep.AMOSTRAS:
          return (
            <AmostrasStep
              data={laudoData.amostras}
              onUpdate={updateAmostras}
              inspection={inspection}
              avaliacaoData={laudoData.avaliacao}
              identificacaoData={laudoData.identificacao}
              fotoFachada={laudoData.vistoria.fotoFachada || ''}
              mode={mode}
              onSkipCalculations={onSkipCalculations}
            />
          );

        case LaudoStep.ASSINATURA:
          return (
            <SignatureStep
              data={laudoData.signature}
              onUpdate={updateSignature}
              dataPreloaded={signatureDataLoaded}
              mode={mode}
            />
          );

        case LaudoStep.TRANSMISSAO:
          return (
            <TransmissaoStep
              data={laudoData.transmissao}
              inspection={inspection}
              onUpdate={updateTransmissao}
              laudoData={laudoData}
              mode={mode}
            />
          );

        default:
          return null;
      }
    };

    return (
      <>
        {avaliacaoStepElement}
        {renderActiveStep()}
      </>
    );
  };

  return { renderStep };
}
